/**
 * LeaChatView — the Lea conversation (shared by the floating panel and the full Lea page).
 *
 * Greeting with the orb, nudges and suggestion chips, then the conversation: Lea answers with text + cards
 * (courses, programs, sessions, skills, goals, plans, communities, posts, people, team members, due items)
 * + actions. An action either sends a suggestion (`say`), opens a page (`href`) or runs a signed action
 * (`token`) — writes such as enrolment, booking, posting or creating a course only ever happen through a
 * tapped action. Attaching a document makes Lea offer what it can do with it (by role). The composer has
 * attach, mode (Standard / AI), dictation and send. The page Lea is opened on is sent along (a community's
 * id, the course being edited) so answers can be about "here".
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUp, ArrowUpRight, BellRing, BookOpen, CalendarClock, CalendarDays, Check, ChevronDown, Compass, GraduationCap,
  Layers, MapPin, MessageSquare, Mic, Paperclip, Plus, Sparkles, Target, UserRound, Users, Users2, X,
} from 'lucide-react';
import { useT, tr, appLocale } from '@/i18n';
import { LeaApi, leaPageContext, type LeaAction, type LeaCard, type LeaConfig, type LeaMode, type LeaNudge, type LeaReply } from '@/services/lea.service';
import styles from './LeaChatView.module.css';

export type ChatMessage =
  | { role: 'user'; text: string }
  | { role: 'lea'; reply: LeaReply }
  | { role: 'note'; text: string }
  | { role: 'error'; text: string };

interface Props {
  conversationId: string | null;
  onConversationId: (id: string) => void;
  /** Bumps when the host wants a fresh conversation (e.g. the "new chat" button). */
  resetKey?: number;
  /** Called after each reply, e.g. to refresh the conversation list. */
  onReply?: () => void;
  onNavigate?: () => void;
  compact?: boolean;
}

const MODE_KEY = 'lea:mode';
const ACCEPT = '.pdf,.txt,.md,.csv,.html,.htm,.docx,.pptx,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation';

/** **bold**, *italic*, "> quote" lines and line breaks — Lea's text is markdown-lite; everything else is escaped by React. */
const inline = (text: string) =>
  text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g).map((chunk, j) => {
    if (chunk.startsWith('**') && chunk.endsWith('**')) return <strong key={j}>{chunk.slice(2, -2)}</strong>;
    if (chunk.length > 2 && chunk.startsWith('*') && chunk.endsWith('*')) return <em key={j}>{chunk.slice(1, -1)}</em>;
    return <React.Fragment key={j}>{chunk}</React.Fragment>;
  });

export const RichText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/\n{2,}/).map((para, i) =>
      para.startsWith('> ') ? (
        <blockquote key={i} className={styles.quote}>{inline(para.replace(/^> ?/gm, ''))}</blockquote>
      ) : (
        <p key={i} className={styles.para}>{inline(para)}</p>
      ),
    )}
  </>
);

export const LeaOrb: React.FC<{ size?: number }> = ({ size = 120 }) => (
  <span className={styles.orb} style={{ width: size, height: size }} aria-hidden="true">
    <span className={styles.orbRing} />
    <span className={styles.orbCore} />
    <span className={styles.orbDust} />
  </span>
);

type Recognition = { start: () => void; stop: () => void; lang: string; interimResults: boolean; continuous: boolean;
  onresult: ((e: any) => void) | null; onend: (() => void) | null; onerror: (() => void) | null };

function speechApi(): (new () => Recognition) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

const CARD_ICONS: Record<string, React.ReactNode> = {
  course: <BookOpen size={16} />,
  program: <GraduationCap size={16} />,
  due: <CalendarClock size={16} />,
  session: <CalendarDays size={16} />,
  skill: <Target size={16} />,
  goal: <Target size={16} />,
  position: <Compass size={16} />,
  pdp: <Layers size={16} />,
  community: <Users2 size={16} />,
  post: <MessageSquare size={16} />,
  person: <UserRound size={16} />,
  member: <Users size={16} />,
};

export const LeaChatView: React.FC<Props> = ({ conversationId, onConversationId, resetKey = 0, onReply, onNavigate, compact }) => {
  const { locale } = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [config, setConfig] = useState<LeaConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nudges, setNudges] = useState<LeaNudge[]>([]);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<LeaMode>(() => (localStorage.getItem(MODE_KEY) === 'ai' ? 'ai' : 'standard'));
  const [modeOpen, setModeOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const recRef = useRef<Recognition | null>(null);
  const modeRef = useRef<HTMLDivElement>(null);
  const canDictate = !!speechApi();
  const dateLocale = appLocale();

  useEffect(() => {
    LeaApi.config(locale)
      .then((cfg) => { setConfig(cfg); setConfigError(null); if (!cfg.ai_available && mode === 'ai') setMode('standard'); })
      .catch((e: Error) => setConfigError(e.message || tr('lea.unavailable', 'Lea is unavailable right now.')));
    LeaApi.nudges(locale).then((r) => setNudges(r.items || [])).catch(() => {});
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the chosen conversation; a new one starts empty.
  useEffect(() => {
    let cancelled = false;
    if (!conversationId) { setMessages([]); return; }
    LeaApi.conversation(locale, conversationId)
      .then((c) => {
        if (cancelled) return;
        setMessages(c.turns.map((t) => (t.role === 'user' ? { role: 'user', text: t.text } : t.payload ? { role: 'lea', reply: t.payload } : { role: 'note', text: t.text })));
      })
      .catch(() => { if (!cancelled) setMessages([]); });
    return () => { cancelled = true; };
  }, [conversationId, locale]);

  useEffect(() => { setMessages([]); }, [resetKey]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages, busy]);
  useEffect(() => { localStorage.setItem(MODE_KEY, mode); }, [mode]);
  useEffect(() => {
    if (!modeOpen) return;
    const onDown = (e: MouseEvent) => { if (modeRef.current && !modeRef.current.contains(e.target as Node)) setModeOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [modeOpen]);

  const takeReply = useCallback((reply: LeaReply) => {
    if (reply.conversation_id && reply.conversation_id !== conversationId) onConversationId(reply.conversation_id);
    setMessages((m) => [...m, { role: 'lea', reply }]);
    onReply?.();
  }, [conversationId, onConversationId, onReply]);

  const send = useCallback(async (body: { message?: string; action?: { token: string } }, echo?: string) => {
    if (busy) return;
    if (echo) setMessages((m) => [...m, { role: 'user', text: echo }]);
    setBusy(true);
    try {
      takeReply(await LeaApi.ask(locale, { ...body, page: leaPageContext(pathname), conversation_id: conversationId, mode }));
    } catch (e: any) {
      setMessages((m) => [...m, { role: 'error', text: e?.message || tr('lea.unavailable', 'Lea is unavailable right now.') }]);
    } finally {
      setBusy(false);
    }
  }, [busy, locale, pathname, conversationId, mode, takeReply]);

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput('');
    send({ message: text }, text);
  };

  const runAction = (a: LeaAction) => {
    if (a.href) { navigate(a.href); onNavigate?.(); return; }
    if (a.token) { send({ action: { token: a.token } }, a.label); return; }
    if (a.say) {
      // A chip that ends with ": " is a prompt to complete (e.g. "Create a goal: …").
      if (/[:：]\s*$/.test(a.say)) { setInput(a.say); inputRef.current?.focus(); return; }
      send({ message: a.say }, a.say);
    }
  };

  const attach = async (file: File) => {
    setBusy(true);
    setMessages((m) => [...m, { role: 'note', text: file.name }]);
    try {
      const reply = await LeaApi.upload(locale, file, conversationId);
      takeReply(reply);
    } catch (e: any) {
      setMessages((m) => [...m, { role: 'error', text: e?.message || tr('lea.unavailable', 'Lea is unavailable right now.') }]);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openNudge = (n: LeaNudge) => {
    LeaApi.nudgeSeen(locale, n.id).catch(() => {});
    setNudges((list) => list.filter((x) => x.id !== n.id));
    if (n.url) { navigate(n.url); onNavigate?.(); }
  };
  const dismissNudge = (n: LeaNudge) => {
    LeaApi.nudgeSeen(locale, n.id).catch(() => {});
    setNudges((list) => list.filter((x) => x.id !== n.id));
  };

  const toggleDictation = () => {
    if (listening) { recRef.current?.stop(); return; }
    const Api = speechApi();
    if (!Api) return;
    const rec = new Api();
    rec.lang = locale === 'ar' ? 'ar-SA' : locale === 'ru' ? 'ru-RU' : 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    const base = input;
    rec.onresult = (e: any) => {
      let text = '';
      for (let i = 0; i < e.results.length; i += 1) text += e.results[i][0].transcript;
      setInput((base ? base + ' ' : '') + text);
    };
    rec.onend = () => { setListening(false); recRef.current = null; inputRef.current?.focus(); };
    rec.onerror = () => { setListening(false); recRef.current = null; };
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString(dateLocale) : '');
  const fmtWhen = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(dateLocale, { dateStyle: 'medium', timeStyle: 'short' }) : '');

  const stateChip = (c: LeaCard) => {
    const s = c.state;
    if (!s) return null;
    const map: Record<string, [string, string]> = {
      overdue: [styles.chipDanger, tr('lea.overdue', 'Overdue')],
      done: [styles.chipOk, tr('lea.state_done', 'Done')],
      booked: [styles.chipOk, tr('lea.state_booked', 'Booked')],
      waitlisted: [styles.chipWarn, tr('lea.state_waitlisted', 'Wait-listed')],
      requested: [styles.chipWarn, tr('lea.state_requested', 'Requested')],
      full: [styles.chipDanger, tr('lea.state_full', 'Full')],
      open: [styles.chipMuted, tr('lea.state_open', 'Open')],
      met: [styles.chipOk, tr('lea.state_met', 'Meets level')],
      gap: [styles.chipDanger, tr('lea.state_gap', 'Gap')],
      unrated: [styles.chipMuted, tr('lea.state_unrated', 'Not rated')],
      member: [styles.chipOk, tr('lea.state_member', 'Member')],
      completed: [styles.chipOk, tr('lea.state_done', 'Done')],
      approved: [styles.chipOk, tr('lea.state_approved', 'Approved')],
      submitted: [styles.chipWarn, tr('lea.state_submitted', 'Submitted')],
      draft: [styles.chipMuted, tr('lea.state_draft', 'Draft')],
      at_risk: [styles.chipDanger, tr('lea.state_at_risk', 'At risk')],
      on_track: [styles.chipOk, tr('lea.state_on_track', 'On track')],
      in_progress: [styles.chipMuted, tr('lea.state_in_progress', 'In progress')],
    };
    const hit = map[s];
    return hit ? <span className={`${styles.chip} ${hit[0]}`}>{hit[1]}</span> : null;
  };

  const cardBody = (c: LeaCard) => {
    if (c.type === 'member') {
      return (
        <>
          <span className={styles.cardIcon} aria-hidden="true">{CARD_ICONS.member}</span>
          <span className={styles.cardMain}>
            <span className={styles.cardTitle}>{c.title}</span>
            {c.subtitle && <span className={styles.cardSub}>{c.subtitle}</span>}
            <span className={styles.cardMeta}>
              {tr('lea.completed_of', '{done} of {total} completed', { done: c.completed ?? 0, total: c.total ?? 0 })}
              {!!c.overdue && <span className={`${styles.chip} ${styles.chipDanger}`}>{tr('lea.overdue_n', '{n} overdue', { n: c.overdue })}</span>}
            </span>
          </span>
        </>
      );
    }
    const showProgress = typeof c.progress === 'number' && ['course', 'program', 'goal', 'pdp', 'position'].includes(c.type);
    const progressLabel = c.type === 'position'
      ? tr('lea.readiness_pct', '{n}% ready', { n: c.progress })
      : tr('lea.progress_pct', '{n}% complete', { n: c.progress });
    return (
      <>
        <span className={styles.cardIcon} aria-hidden="true">{CARD_ICONS[c.type] ?? CARD_ICONS.course}</span>
        <span className={styles.cardMain}>
          <span className={styles.cardTitle}>{c.title}</span>
          {c.subtitle && c.type !== 'goal' && <span className={styles.cardSub}>{c.subtitle}</span>}
          {c.summary && <span className={styles.cardSub}>{c.summary}</span>}
          {c.type === 'skill' && (
            <span className={styles.levels}>
              <span>{c.current_level || tr('lea.level_none', '—')}</span>
              <span className={styles.levelsArrow} aria-hidden="true">→</span>
              <span>{c.required_level || tr('lea.level_none', '—')}</span>
            </span>
          )}
          {showProgress && (
            <span className={styles.progress} aria-label={progressLabel}>
              <span className={styles.progressFill} style={{ width: `${Math.min(100, Math.max(0, c.progress as number))}%` }} />
            </span>
          )}
          <span className={styles.cardMeta}>
            {showProgress && <span>{progressLabel}</span>}
            {c.type === 'goal' && c.subtitle && <span>{tr('lea.weight', 'Weight {w}', { w: c.subtitle })}</span>}
            {c.type === 'session' && c.starts_at && <span>{fmtWhen(c.starts_at)}</span>}
            {c.type === 'session' && c.location && <span className={styles.metaIcon}><MapPin size={12} aria-hidden="true" />{c.location}</span>}
            {c.type === 'session' && c.trainer && <span>{c.trainer}</span>}
            {c.type === 'session' && typeof c.seats_left === 'number' && <span>{tr('lea.seats_left', '{n} seats left', { n: c.seats_left })}</span>}
            {c.type === 'community' && typeof c.members === 'number' && <span>{tr('lea.members_n', '{n} members', { n: c.members })}</span>}
            {c.type === 'post' && c.created_at && <span>{fmtDate(c.created_at)}</span>}
            {c.type === 'post' && typeof c.comments === 'number' && <span>{tr('lea.comments_n', '{n} comments', { n: c.comments })}</span>}
            {c.due_date && c.type !== 'session' && <span>{tr('lea.due_on', 'Due {date}', { date: fmtDate(c.due_date) })}</span>}
            {!!c.duration_minutes && <span>{tr('lea.minutes', '{n} min', { n: c.duration_minutes })}</span>}
            {stateChip(c)}
          </span>
        </span>
      </>
    );
  };

  const name = config?.name || '';
  const greeting = config?.greeting?.split('\n') || [];
  const empty = messages.length === 0;

  return (
    <div className={`${styles.view} ${compact ? styles.compact : ''}`} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className={styles.scroll}>
        {empty && (
          <div className={styles.hero}>
            <LeaOrb size={compact ? 110 : 140} />
            {configError ? (
              <p className={styles.heroError}>{configError}</p>
            ) : (
              <h2 className={styles.heroTitle}>
                <span>{greeting[0] || (name ? tr('lea.hey_name', 'Hey {name}.', { name }) : tr('lea.hey', 'Hey.'))}</span>
                <span className={styles.heroGlow}>{greeting[1] || ''}</span>
              </h2>
            )}
            {nudges.length > 0 && (
              <ul className={styles.nudges} aria-label={tr('lea.nudges', 'Reminders from Lea')}>
                {nudges.slice(0, 3).map((n) => (
                  <li key={n.id} className={styles.nudge}>
                    <BellRing size={15} aria-hidden="true" />
                    <button type="button" className={styles.nudgeText} onClick={() => openNudge(n)}>{n.text}</button>
                    <button type="button" className={styles.nudgeClose} onClick={() => dismissNudge(n)} aria-label={tr('common.dismiss', 'Dismiss')}><X size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
            {config && (
              <div className={styles.chips}>
                {!!leaPageContext(pathname).community_id && (
                  <button type="button" className={styles.suggestion} onClick={() => runAction({ label: tr('lea.chip_whats_new', "What's new here?"), say: tr('lea.chip_whats_new', "What's new here?") })} disabled={busy}>
                    <ArrowUpRight size={15} aria-hidden="true" />
                    {tr('lea.chip_whats_new', "What's new here?")}
                  </button>
                )}
                {config.suggestions.map((s) => (
                  <button key={s.label} type="button" className={styles.suggestion} onClick={() => runAction(s)} disabled={busy}>
                    <ArrowUpRight size={15} aria-hidden="true" />
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((m, i) => {
          if (m.role === 'user') return <div key={i} className={styles.userRow}><div className={styles.userBubble}>{m.text}</div></div>;
          if (m.role === 'note') return <div key={i} className={styles.noteRow}><div className={styles.note}><Paperclip size={14} aria-hidden="true" />{m.text}</div></div>;
          if (m.role === 'error') return <div key={i} className={styles.leaRow}><div className={`${styles.leaBubble} ${styles.errorBubble}`}>{m.text}</div></div>;
          const r = m.reply;
          return (
            <div key={i} className={styles.leaRow}>
              <div className={styles.leaBubble} dir={r.language === 'ar' ? 'rtl' : 'ltr'}>
                {r.ai_used && <span className={styles.aiTag}><Sparkles size={12} aria-hidden="true" />AI</span>}
                {r.text && <RichText text={r.text} />}
                {r.cards.length > 0 && (
                  <ul className={styles.cards}>
                    {r.cards.map((c, j) => (
                      <li key={j} className={styles.card}>
                        {c.url ? (
                          <button type="button" className={styles.cardLink} onClick={() => runAction({ label: c.title || '', href: c.url })}>{cardBody(c)}</button>
                        ) : <div className={styles.cardLink}>{cardBody(c)}</div>}
                        {c.actions?.length ? (
                          <span className={styles.cardActions}>
                            {c.actions.map((a, k) => (
                              <button key={k} type="button" className={`${styles.actionBtn} ${a.style === 'primary' ? styles.actionPrimary : ''} ${a.style === 'danger' ? styles.actionDanger : ''}`} onClick={() => runAction(a)} disabled={busy}>{a.label}</button>
                            ))}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
                {r.actions.length > 0 && (
                  <span className={styles.actions}>
                    {r.actions.map((a, k) => (
                      <button key={k} type="button" className={`${styles.actionBtn} ${a.style === 'primary' ? styles.actionPrimary : ''} ${a.style === 'danger' ? styles.actionDanger : ''}`} onClick={() => runAction(a)} disabled={busy && !a.href}>
                        {a.label}
                      </button>
                    ))}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {busy && <div className={styles.leaRow}><div className={styles.typing} aria-label={tr('lea.thinking', 'Lea is thinking')}><i /><i /><i /></div></div>}
        <div ref={endRef} />
      </div>

      <form className={styles.composer} onSubmit={submit}>
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder={tr('lea.placeholder_long', "What's not working right now?")}
          aria-label={tr('lea.placeholder', 'Ask Lea…')}
          className={styles.input}
        />
        <div className={styles.composerRow}>
          <input ref={fileRef} type="file" hidden accept={ACCEPT} onChange={(e) => { const f = e.target.files?.[0]; if (f) attach(f); }} />
          <button type="button" className={styles.round} onClick={() => fileRef.current?.click()} aria-label={tr('lea.attach', 'Attach a document')} title={tr('lea.attach_hint', 'Attach a PDF, Word, PowerPoint or text file')} disabled={busy}>
            <Plus size={18} />
          </button>
          <span className={styles.spacer} />
          <div className={styles.modeWrap} ref={modeRef}>
            <button type="button" className={styles.modeBtn} onClick={() => setModeOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={modeOpen}>
              {mode === 'ai' ? tr('lea.mode_ai', 'AI') : tr('lea.mode_standard', 'Standard')}
              <ChevronDown size={16} aria-hidden="true" />
            </button>
            {modeOpen && (
              <ul className={styles.modeMenu} role="listbox">
                {([['standard', tr('lea.mode_standard', 'Standard'), tr('lea.mode_standard_hint', 'Fast answers from your learning data')],
                   ['ai', tr('lea.mode_ai', 'AI'), config?.ai_available ? tr('lea.mode_ai_hint', 'Free-form answers, documents and explanations') : tr('lea.mode_ai_off', 'Not set up yet — an admin must add an AI model in the Lea console')]] as const).map(([value, label, hint]) => (
                  <li key={value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={mode === value}
                      className={styles.modeItem}
                      disabled={value === 'ai' && !config?.ai_available}
                      onClick={() => { setMode(value); setModeOpen(false); }}
                    >
                      <span className={styles.modeCheck}>{mode === value && <Check size={14} />}</span>
                      <span><strong>{label}</strong><small>{hint}</small></span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {canDictate && (
            <button type="button" className={`${styles.round} ${listening ? styles.listening : ''}`} onClick={toggleDictation} aria-label={listening ? tr('lea.stop_dictation', 'Stop dictation') : tr('lea.dictate', 'Dictate')} aria-pressed={listening}>
              <Mic size={18} />
            </button>
          )}
          <button type="submit" className={`${styles.round} ${styles.send} ${input.trim() ? styles.sendReady : ''}`} disabled={!input.trim() || busy} aria-label={tr('lea.send', 'Send')}>
            <ArrowUp size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeaChatView;
